import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CAPABILITY_FLAGS,
  PLAYER_CLASS_PROFILES,
  PlayerClasses,
  capabilityLabel,
  type ComposerKind,
  type ComposerSummary,
  type PlayerClass,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { ApiError } from '@/api/client';
import { createComposer } from '@/api/composers';

/**
 * `/composers/new` — single-step wizard (Phase 1).
 *
 *   Step 1: name (FR required + EN optional), kind selector (only "loop"
 *           enabled). On submit, POST to /composers; on success, navigate
 *           to `/composers/:id` for the program editor.
 *
 * Multi-step (programs, items) is handled on the detail page rather than
 * gated behind a wizard — the detail page is the editor.
 */

interface FormState {
  nameFr: string;
  nameEn: string;
  kind: ComposerKind;
  /** '' = no design class chosen → byte-identical to the pre-P3 flow. */
  designClass: PlayerClass | '';
  widthPx: string;
  heightPx: string;
  /** Selected min_capability_flags (only meaningful when designClass!==''). */
  flags: string[];
}

const INITIAL: FormState = {
  nameFr: '',
  nameEn: '',
  kind: 'loop',
  designClass: '',
  widthPx: '',
  heightPx: '',
  flags: [],
};

export function ComposerNewPage(): JSX.Element {
  const { t, i18n } = useTranslation(['composers', 'common']);
  const lang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const mutation = useMutation<ComposerSummary, ApiError, void>({
    mutationFn: async () => {
      if (form.nameFr.trim() === '') {
        throw new ApiError(400, {
          message: 'composers:new.errors.missing_name_fr',
        });
      }
      const name_i18n =
        form.nameEn.trim() === ''
          ? { fr: form.nameFr.trim() }
          : { fr: form.nameFr.trim(), en: form.nameEn.trim() };
      // Conditional-spread: when no design class is chosen, the key is
      // ABSENT — createComposer's request body stays byte-identical to the
      // pre-P3 shape (back-compat; `exactOptionalPropertyTypes`-safe).
      return createComposer({
        name_i18n,
        kind: form.kind,
        ...(form.designClass !== ''
          ? { min_capability_flags: form.flags }
          : {}),
      });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['composers', 'list'] });
      navigate(`/composers/${created.id}`);
    },
    onError: (err) => {
      setErrorKey(err.message);
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setErrorKey(null);
      mutation.mutate();
    },
    [mutation],
  );

  // Selecting a design class prefills the reference resolution and seeds
  // the min_capability_flags selection from PLAYER_CLASS_PROFILES; the
  // neutral '' option resets all three back to the pre-P3 (empty) state.
  const handleDesignClassChange = useCallback((value: string) => {
    if (value === '') {
      setForm((prev) => ({
        ...prev,
        designClass: '',
        widthPx: '',
        heightPx: '',
        flags: [],
      }));
      return;
    }
    const c = value as PlayerClass;
    const profile = PLAYER_CLASS_PROFILES[c];
    setForm((prev) => ({
      ...prev,
      designClass: c,
      widthPx: String(profile.default_resolution_px.w),
      heightPx: String(profile.default_resolution_px.h),
      flags: [...profile.expected_capability_flags],
    }));
  }, []);

  const toggleFlag = useCallback((flag: string) => {
    setForm((prev) => ({
      ...prev,
      flags: prev.flags.includes(flag)
        ? prev.flags.filter((f) => f !== flag)
        : [...prev.flags, flag],
    }));
  }, []);

  const canSubmit =
    !mutation.isPending && form.nameFr.trim().length > 0;

  return (
    <div className="flex flex-col gap-6" data-testid="composer-new-page">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('composers:new.page_title')}
        </h1>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex max-w-xl flex-col gap-4 rounded-md border border-border bg-card p-4"
        noValidate
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('composers:new.name_fr_label')}
          </span>
          <Input
            type="text"
            required
            value={form.nameFr}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, nameFr: e.target.value }))
            }
            placeholder={t('composers:new.name_fr_placeholder')}
            data-testid="composer-new-name-fr"
            disabled={mutation.isPending}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('composers:new.name_en_label')}
          </span>
          <Input
            type="text"
            value={form.nameEn}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, nameEn: e.target.value }))
            }
            placeholder={t('composers:new.name_en_placeholder')}
            data-testid="composer-new-name-en"
            disabled={mutation.isPending}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('composers:new.kind_label')}</span>
          <Select
            value={form.kind}
            onValueChange={(v) =>
              setForm((prev) => ({ ...prev, kind: v as ComposerKind }))
            }
            disabled={mutation.isPending}
          >
            <SelectTrigger className="h-10 w-full" data-testid="composer-new-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="loop">{t('composers:new.kind_loop')}</SelectItem>
            </SelectContent>
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('composers:classprofile.design_for_label')}
          </span>
          <Select
            value={form.designClass === '' ? '__none__' : form.designClass}
            onValueChange={(v) =>
              handleDesignClassChange(v === '__none__' ? '' : v)
            }
            disabled={mutation.isPending}
          >
            <SelectTrigger
              className="h-10 w-full"
              data-testid="composer-design-class"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                {t('composers:classprofile.design_for_none')}
              </SelectItem>
              {PlayerClasses.map((c) => (
                <SelectItem key={c} value={c}>
                  {PLAYER_CLASS_PROFILES[c].label_i18n[lang]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        {form.designClass !== '' && (
          <fieldset className="flex flex-col gap-3 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium">
              {PLAYER_CLASS_PROFILES[form.designClass].label_i18n[lang]}
            </legend>

            <div className="flex flex-wrap gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('composers:layout_editor.width')}
                </span>
                <Input
                  type="number"
                  min={1}
                  value={form.widthPx}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, widthPx: e.target.value }))
                  }
                  className="w-32"
                  data-testid="composer-design-width"
                  disabled={mutation.isPending}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('composers:layout_editor.height')}
                </span>
                <Input
                  type="number"
                  min={1}
                  value={form.heightPx}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, heightPx: e.target.value }))
                  }
                  className="w-32"
                  data-testid="composer-design-height"
                  disabled={mutation.isPending}
                />
              </label>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                {t('composers:classprofile.flags_label')}
              </span>
              <div className="flex flex-col gap-1">
                {CAPABILITY_FLAGS.map((c) => (
                  <label
                    key={c.flag}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={form.flags.includes(c.flag)}
                      onChange={() => toggleFlag(c.flag)}
                      data-testid={`composer-design-flag-${c.flag}`}
                      disabled={mutation.isPending}
                    />
                    <span>{capabilityLabel(c.flag, lang)}</span>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>
        )}

        {errorKey && (
          <Alert variant="danger" data-testid="composer-new-error">
            {translateError((k) => t(k) as string, errorKey)}
          </Alert>
        )}

        <footer className="mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/composers')}
            disabled={mutation.isPending}
            data-testid="composer-new-cancel"
          >
            {t('composers:new.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="composer-new-submit"
          >
            {mutation.isPending
              ? t('composers:new.submitting')
              : t('composers:new.submit')}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function translateError(
  t: (key: string) => string,
  key: string,
): string {
  const translated = t(key);
  if (translated !== key && translated.length > 0) return translated;
  for (const ns of ['composers', 'errors', 'common']) {
    const guess = t(`${ns}:${key}`);
    if (guess !== `${ns}:${key}` && guess !== key) return guess;
  }
  return t('composers:new.errors.create_failed');
}

export default ComposerNewPage;
